import { useEffect, useState } from "react";
import { Briefcase, ListChecks, Route } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

/**
 * modo:
 * - entrada: elegir proyecto (+ tarea) al fichar entrada
 * - cambio: cambiar de proyecto (y tarea)
 * - tarea: cambiar solo la tarea del proyecto actual
 */
export default function ProyectoImputacionPanel({
  proyectos = [],
  enviando = false,
  onConfirm,
  onCancel,
  modo = "entrada",
  permitirSinProyecto = modo === "entrada",
  proyectoFijoId = null,
  tareaActualId = null,
  facturableActual = true,
}) {
  const proyectoInicial =
    modo === "tarea" && proyectoFijoId
      ? String(proyectoFijoId)
      : "";
  const [proyectoId, setProyectoId] = useState(proyectoInicial);
  const [tareaId, setTareaId] = useState(
    modo === "tarea" && tareaActualId ? String(tareaActualId) : "",
  );
  const [facturable, setFacturable] = useState(facturableActual !== false);
  const proyecto = proyectos.find((p) => String(p.id) === proyectoId);

  useEffect(() => {
    if (modo === "tarea" && proyectoFijoId) {
      setProyectoId(String(proyectoFijoId));
    }
  }, [modo, proyectoFijoId]);

  useEffect(() => {
    if (modo === "tarea") return;
    setTareaId("");
    if (proyecto?.tareas?.length) {
      setFacturable(proyecto.tareas[0].facturable_default !== false);
    }
  }, [proyectoId, proyecto?.tareas, modo]);

  const seleccionarTarea = (id) => {
    setTareaId(id);
    const tarea = proyecto?.tareas?.find((t) => String(t.id) === id);
    if (tarea) setFacturable(tarea.facturable_default !== false);
  };

  const hayRutas = proyectos.some((p) => p.es_ruta);
  const titulo =
    modo === "tarea" ? "Cambiar tarea" : modo === "cambio" ? (hayRutas ? "Cambiar proyecto o ruta" : "Cambiar proyecto") : "Imputar entrada";
  const Icono = modo === "tarea" ? ListChecks : hayRutas ? Route : Briefcase;
  const puedeConfirmar =
    modo === "tarea"
      ? Boolean(proyectoId)
      : modo === "cambio"
        ? Boolean(proyectoId && proyectoId !== "sin-proyecto")
        : true;

  return (
    <div className="w-full max-w-sm rounded-2xl border border-violet-200 bg-white p-4 shadow-md" data-testid="proyecto-imputacion-panel">
      <div className="flex items-center justify-center gap-2 text-violet-800">
        <Icono className="h-4 w-4" />
        <p className="text-sm font-semibold">{titulo}</p>
      </div>
      <div className="mt-4 space-y-3">
        {modo === "tarea" ? (
          <div className="rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2 text-sm">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Proyecto</p>
            <p className="font-medium text-zinc-900">
              {proyecto?.codigo ? `${proyecto.codigo} · ` : ""}
              {proyecto?.nombre || "—"}
            </p>
          </div>
        ) : (
          <div>
            <Label>{hayRutas ? "Proyecto o ruta" : "Proyecto"} {modo === "cambio" ? "*" : ""}</Label>
            <Select value={proyectoId} onValueChange={setProyectoId}>
              <SelectTrigger>
                <SelectValue placeholder={hayRutas ? "Selecciona un proyecto o ruta" : "Selecciona un proyecto"} />
              </SelectTrigger>
              <SelectContent>
                {permitirSinProyecto && <SelectItem value="sin-proyecto">Sin proyecto</SelectItem>}
                {proyectos.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.es_ruta ? "Ruta · " : p.codigo ? `${p.codigo} · ` : ""}
                    {p.nombre}
                    {p.cliente_nombre ? ` · ${p.cliente_nombre}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {proyecto?.es_ruta && (
              <p className="mt-1.5 text-[11px] text-rose-700">
                El tiempo se imputará a esta ruta comercial.
              </p>
            )}
          </div>
        )}

        {proyecto && proyectoId !== "sin-proyecto" && !proyecto.es_ruta && (
          <>
            {proyecto.tareas?.length > 0 ? (
              <div>
                <Label>Tarea / fase {modo === "tarea" ? "*" : ""}</Label>
                <Select value={tareaId || "sin-tarea"} onValueChange={seleccionarTarea}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sin tarea específica" />
                  </SelectTrigger>
                  <SelectContent>
                    {modo !== "tarea" && <SelectItem value="sin-tarea">Sin tarea específica</SelectItem>}
                    {proyecto.tareas.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.codigo ? `${t.codigo} · ` : ""}
                        {t.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              modo === "tarea" && (
                <p className="text-sm text-zinc-500">Este proyecto no tiene tareas definidas.</p>
              )
            )}
            <div className="flex items-center gap-2 rounded-lg border border-violet-100 bg-violet-50/50 p-3">
              <Switch checked={facturable} onCheckedChange={setFacturable} id="imputacion-facturable" />
              <Label htmlFor="imputacion-facturable" className="cursor-pointer">
                Facturable
              </Label>
            </div>
          </>
        )}
      </div>
      <div className="mt-4 flex gap-2">
        <Button variant="outline" className="flex-1" onClick={onCancel} disabled={enviando}>
          Cancelar
        </Button>
        <Button
          className="flex-1"
          disabled={
            enviando ||
            !puedeConfirmar ||
            (modo === "tarea" && (!tareaId || tareaId === "sin-tarea") && !proyecto?.tareas?.length)
          }
          onClick={() => {
            if (proyecto?.es_ruta) {
              onConfirm({
                ruta_comercial_id: proyecto.ruta_comercial_id,
                proyecto_id: null,
                tarea_id: null,
                facturable: false,
              });
              return;
            }
            onConfirm(
              proyectoId && proyectoId !== "sin-proyecto"
                ? {
                    proyecto_id: Number(proyectoId),
                    tarea_id: tareaId && tareaId !== "sin-tarea" ? Number(tareaId) : null,
                    facturable,
                  }
                : null,
            );
          }}
        >
          {modo === "entrada" ? "Confirmar" : "Cambiar"}
        </Button>
      </div>
    </div>
  );
}
